import { useState } from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Badge from 'design-system/src/components/Badge/Badge';
import {
  GeneralInfoTable,
  type GeneralInfoTableProps,
} from 'design-system/src/components/GeneralInfoTable/GeneralInfoTable';
import { Sidebar, SidebarTopBar } from 'design-system/src/components/Sidebar/Sidebar';
import { DrugPanel, PatientPanel, PharmacyPanel, PrescriberPanel } from '../../../sidebar/SidebarPanels';
import { ItemViewTopNav } from '../../../tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../../../tableItemDetailViewComponents/Containers';
import { BusinessRelationshipSmallPrint } from '../../../businessRelationshipNotices/BusinessRelationshipSmallPrint/BusinessRelationshipSmallPrint';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import type { Drug, OutboundTaskType, Patient, Pharmacy, Prescriber } from '../../../../api';

export type StructuredTaskViewWrapperSidebarProps = {
  /** When provided, the Drug panel is shown (requires data). */
  drug?: {
    /** Drug entity data. When present, the DrugPanel is rendered. */
    data?: Drug;
    /** When true, DrugPanel shows the business relationship notice. */
    showBusinessRelationshipNotice?: boolean;
    /** Custom copy for the business relationship notice in DrugPanel. */
    businessRelationshipNoticeCopy?: string;
  };
  /** When provided, the Patient panel is shown. */
  patient?: Patient;
  /** When provided, the Prescriber panel is shown. */
  prescriber?: Prescriber;
  /** When provided, the Pharmacy panel is shown. */
  pharmacy?: {
    /** Pharmacy entity data. */
    data?: Pharmacy;
    /** When true, the update pharmacy button is shown. */
    showUpdateButton?: boolean;
    /** Called when pharmacy is updated in Pharmacy panel. */
    onUpdate?: () => void;
  };
};

export type StructuredTaskViewWrapperProps = {
  /** Task type label shown as the page heading. */
  taskType: OutboundTaskType;
  /** Props passed to the GeneralInfoTable above the main content. */
  generalInfoTableProps: GeneralInfoTableProps;
  /** Task creation date to display in the info table. */
  taskCreatedAt: string;
  /** When true, renders a "Closed" badge in the top nav. */
  isCompleted?: boolean;
  /** Optional content rendered in the top nav (e.g. additional badges). */
  additionalRightSideContent?: React.ReactNode;
  /** Optional content rendered above the heading (e.g. expiration countdown). */
  aboveHeadingContent?: React.ReactNode;
  /** Main content rendered inside the centered content area, below the info table. */
  children: React.ReactNode;
  /** When true, BusinessRelationshipSmallPrint is rendered below the children. */
  showBusinessRelationshipSmallPrint?: boolean;
  /**
   * Sidebar entity data; each panel is shown only when the corresponding value is present.
   * Pharmacy section is also shown when `pharmacy.showUpdateButton` is true.
   * If all sections are hidden, the sidebar is not shown at all.
   * The sidebar's visibility is also controlled by a button in the top nav.
   */
  sidebar?: StructuredTaskViewWrapperSidebarProps;
  /** Optional class name for the root container. */
  className?: string;
};

/**
 * Shared layout for structured task views: top nav, task type heading, GeneralInfoTable,
 * optional sidebar (with panels shown based on provided data), and optional business relationship small print.
 */
export const StructuredTaskViewWrapper: React.FC<StructuredTaskViewWrapperProps> = ({
  taskType,
  generalInfoTableProps,
  taskCreatedAt,
  isCompleted = false,
  additionalRightSideContent,
  aboveHeadingContent,
  children,
  showBusinessRelationshipSmallPrint = false,
  sidebar,
  className,
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const showPharmacyPanel = sidebar && (sidebar.pharmacy?.data != null || sidebar.pharmacy?.showUpdateButton === true);
  const hasSidebar =
    sidebar &&
    (sidebar.drug?.data != null ||
      sidebar.patient != null ||
      sidebar.prescriber != null ||
      sidebar.pharmacy?.data != null ||
      sidebar.pharmacy?.showUpdateButton === true);

  // Merge task created date with any existing extraData
  const mergedExtraData = [
    ...(generalInfoTableProps.extraData || []),
    { label: 'Requested date', value: formatStandardDate(taskCreatedAt) },
  ];

  const rightSideContent = (
    <>
      {isCompleted && <Badge variant="neutral">Closed</Badge>}
      {additionalRightSideContent}
    </>
  );

  return (
    <BodyContainer className={className}>
      <TopNavAndContent>
        <ItemViewTopNav
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          noSidebar={!hasSidebar}
          additionalRightSideContent={rightSideContent}
        />

        <ContentOuterWrapper>
          <CenteredContent>
            {aboveHeadingContent}
            <Typography styledAs="h6" renderedAs="h1" marginBottom="1rem">
              {taskType}
            </Typography>

            <GeneralInfoTable {...generalInfoTableProps} extraData={mergedExtraData} minFieldLabelWidth="132px" />

            {children}

            {showBusinessRelationshipSmallPrint && <BusinessRelationshipSmallPrint />}
          </CenteredContent>
        </ContentOuterWrapper>
      </TopNavAndContent>

      {hasSidebar && !isSidebarCollapsed && (
        <Sidebar>
          <SidebarTopBar />
          {sidebar.drug?.data != null && (
            <DrugPanel
              drug={sidebar.drug.data}
              showBusinessRelationshipNotice={sidebar.drug.showBusinessRelationshipNotice}
              businessRelationshipNoticeCopy={sidebar.drug.businessRelationshipNoticeCopy}
            />
          )}
          {showPharmacyPanel && (
            <PharmacyPanel
              pharmacy={sidebar.pharmacy?.data}
              showUpdatePharmacyButton={sidebar.pharmacy?.showUpdateButton}
              rxId={sidebar.drug?.data?.id}
              onPharmacyUpdate={sidebar.pharmacy?.onUpdate}
            />
          )}
          {sidebar.patient != null && <PatientPanel patient={sidebar.patient} />}
          {sidebar.prescriber != null && <PrescriberPanel prescriber={sidebar.prescriber} />}
        </Sidebar>
      )}
    </BodyContainer>
  );
};

export default StructuredTaskViewWrapper;
